import React from "react";
import { StyleSheet, ScrollView } from "react-native";
import { Tab2 } from "../../../../../common/native";
import FormattingContent from "./FormattingContent";
import FontContent from "./FontContent";
import ColorContent from "./ColorContent";
import { TTemplate } from "../../../../../../utils/types";
import { MAX_ZINDEX } from "../../../../../../utils/constant";

type AddTextModalContentProps = {
  currentTemplate: TTemplate;
  currentTextEditingIndex: number | null;
  setCurrentTemplate: (template: TTemplate) => void;
  onDeleteText?: () => void;
};

const AddTextModalContent: React.FC<AddTextModalContentProps> = ({
  currentTemplate,
  setCurrentTemplate,
  currentTextEditingIndex,
  onDeleteText,
}) => {
  const [tab, setTab] = React.useState<string>("Fomatting");

  const [textContent, setTextContent] =
    React.useState<string>("Your text here");
  const [textFontSize, setTextFontSize] = React.useState<number>(16);
  const [textAlignment, setTextAlignment] = React.useState<
    "left" | "center" | "right"
  >("center");
  const [textWrap, setTextWrap] = React.useState<"nowrap" | "wrap">("nowrap");
  const [textWidth, setTextWidth] = React.useState<number | "initial">(
    "initial",
  );
  const [formats, setFormats] = React.useState<{
    bold: boolean;
    italic: boolean;
    underline: boolean;
  }>({
    bold: false,
    italic: false,
    underline: false,
  });
  const [textFont, setTextFont] = React.useState<string>("Arial");
  const [textColor, setTextColor] = React.useState<string>("#ffffff");

  // This useRef is used to run the useEffect only once
  // when the component is mounted. This useRef is not
  // needed in prod when React Strict Mode is enabled.
  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (currentTextEditingIndex !== null) {
      const textToEdit = currentTemplate?.texts?.[currentTextEditingIndex];

      if (textToEdit) {
        if (textToEdit.textStyle.transform === "translateX(-50%)") {
          setTextAlignment("center");
        }
        if (textToEdit.textStyle.transform === "translateX(-100%)") {
          setTextAlignment("right");
        }
        if (textToEdit.textStyle.transform === "translateX(0%)") {
          setTextAlignment("left");
        }
        if (!textToEdit.textStyle.transform) {
          setTextAlignment("left");
        }
        setTextContent(textToEdit.content);
        // @ts-ignore
        setTextFontSize(textToEdit.textStyle.fontSize || "initial");
        // @ts-ignore
        setTextWrap(textToEdit.textStyle.textWrap || "initial");
        // @ts-ignore
        setTextWidth(textToEdit.textStyle.width || "initial");
        setTextColor(textToEdit.textStyle.color || "initial");
        setTextFont(textToEdit.textStyle.fontFamily || "initial");
        setFormats({
          bold: textToEdit.textStyle.fontWeight === "bold",
          italic: textToEdit.textStyle.fontStyle === "italic",
          underline: textToEdit.textStyle.textDecoration === "underline",
        });
      }
    } else {
      // @ts-ignore
      setCurrentTemplate((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          texts: [
            ...prev.texts,
            {
              content: "Your text here",
              position: { x: 250, y: 250 },
              containerStyle: {
                // IMPORTANT NOTE for template creater: DO NOT use use zIndex
                // in the range (MAX_ZINDEX, MAX_ZINDEX + 10)
                // for images or texts, as the range is reserved for coder.
                zIndex: MAX_ZINDEX - 10,
              },
              textStyle: {
                fontSize: 16,
                width: "initial",
                color: "#ffffff",
                textWrap: "nowrap",
                whiteSpace: "nowrap",
                fontFamily: "Arial",
                textAlign: "center",
                fontStyle: "normal",
                position: "absolute",
                fontWeight: "normal",
                textDecoration: "none",
                transform: "translateX(-50%)",
              },
            },
          ],
        };
      });
    }
  }, []);

  React.useEffect(() => {
    // @ts-ignore
    setCurrentTemplate((prev) => {
      if (!prev || prev.texts.length === 0) return prev;

      return {
        ...prev,
        texts: prev.texts.map((text: { textStyle: any }, index: number) =>
          index === (currentTextEditingIndex ?? prev.texts.length - 1) // Use currentTextEditingIndex if available, else use last text
            ? {
                ...text,
                content: textContent,
                ...(currentTextEditingIndex === null && {
                  position: { x: 250, y: 250 },
                }),
                ...(currentTextEditingIndex === null && {
                  containerStyle: {
                    zIndex: MAX_ZINDEX - 1,
                  },
                }),
                textStyle: {
                  ...text.textStyle,
                  position: "absolute",
                  width: textWidth,
                  color: textColor,
                  textWrap: textWrap,
                  whiteSpace: textWrap === "wrap" ? "normal" : "nowrap",
                  overflowWrap: textWrap === "wrap" ? "anywhere" : "normal",
                  fontSize: textFontSize,
                  textAlign: textAlignment,
                  fontFamily: textFont,
                  fontWeight: formats.bold ? "bold" : "normal",
                  fontStyle: formats.italic ? "italic" : "normal",
                  textDecoration: formats.underline ? "underline" : "none",
                  transform:
                    textAlignment === "center"
                      ? "translateX(-50%)"
                      : textAlignment === "right"
                        ? "translateX(-100%)"
                        : "translateX(0%)",
                },
              }
            : text,
        ),
      };
    });
  }, [
    currentTextEditingIndex,
    formats,
    textWrap,
    textFont,
    textColor,
    textWidth,
    textContent,
    textFontSize,
    textAlignment,
  ]);

  return (
    <ScrollView
      style={styles.scrollContainer}
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
      bounces={false}
    >
      <Tab2
        tabs={["Fomatting", "Font", "Color"]}
        initialTab="Fomatting"
        onTabSwitch={setTab}
        tabWidth={72}
        tabPadding={4}
        fontSize={12}
      />

      {tab === "Fomatting" && (
        <FormattingContent
          formats={formats}
          textWrap={textWrap}
          textWidth={textWidth}
          fontSize={textFontSize}
          textContent={textContent}
          textAlignment={textAlignment}
          setFormats={setFormats}
          setTextWrap={setTextWrap}
          setTextWidth={setTextWidth}
          setFontSize={setTextFontSize}
          setTextContent={setTextContent}
          setTextAlignment={setTextAlignment}
          autoFocusText={tab === "Fomatting"}
          showDeleteText={
            !!onDeleteText &&
            (currentTextEditingIndex !== null ||
              (currentTemplate.texts && currentTemplate.texts.length > 0))
          }
          onDeleteText={onDeleteText}
        />
      )}
      {tab === "Font" && (
        <FontContent textFont={textFont} onSelect={setTextFont} />
      )}
      {tab === "Color" && (
        <ColorContent textColor={textColor} onSelect={setTextColor} />
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    maxHeight: 280,
  },
  container: {
    gap: 8,
    paddingBottom: 8,
  },
});

export default AddTextModalContent;
